import type { ColumnDef } from '@vnetwork/ui/data';
import { StatusPill } from '@vnetwork/ui/data';
import type { EquipmentMapping } from '@/api/capacities-cloud';
import { useEquipmentMappings } from '@/api/capacities-cloud';
import { cloudStatusView } from './cloud-format';
import { CloudTableSection } from './cloud-table-section';

const muted = { color: 'var(--vn-fg-muted)', fontStyle: 'italic' } as const;
const dash = <span style={muted}>—</span>;

const str = (v: unknown): string | undefined =>
  typeof v === 'string' && v.length > 0 ? v : undefined;

const mappingColumns: ColumnDef<EquipmentMapping, unknown>[] = [
  {
    id: 'id',
    header: 'Mapping',
    cell: ({ row }) => (
      <span style={{ overflowWrap: 'anywhere', fontFamily: 'var(--vn-font-mono, monospace)' }}>
        {row.original.id}
      </span>
    ),
  },
  {
    id: 'provider',
    header: 'Provider',
    cell: ({ row }) => {
      const label = str(row.original.provider_name) ?? str(row.original.provider_code);
      return label ? <span>{label}</span> : dash;
    },
  },
  {
    id: 'provider_type',
    header: 'Type',
    cell: ({ row }) => (row.original.provider_type ? <span>{row.original.provider_type}</span> : dash),
  },
  {
    id: 'zone',
    header: 'Zone / Host',
    cell: ({ row }) => {
      const label = str(row.original.zone_id) ?? str(row.original.host_id);
      return label ? (
        <span style={{ overflowWrap: 'anywhere' }}>{label}</span>
      ) : (
        dash
      );
    },
  },
  {
    id: 'active',
    header: 'Status',
    cell: ({ row }) => {
      const active = row.original.is_active;
      if (active == null) return dash;
      const v = cloudStatusView(active ? 'active' : 'error');
      return <StatusPill tone={v.tone}>{active ? 'Active' : 'Inactive'}</StatusPill>;
    },
  },
];

/**
 * Equipment ↔ compute-host mapping table. The `/capacity/mappings` endpoint
 * returns 404 on staging; the section degrades to a retryable Alert / EmptyState
 * rather than crashing. Read-only. Embed on a page or tab.
 */
export function CloudMappingSection() {
  const { data, isLoading, isError, refetch } = useEquipmentMappings();
  return (
    <CloudTableSection
      columns={mappingColumns}
      data={data}
      isLoading={isLoading}
      isError={isError}
      onRetry={() => refetch()}
      getRowId={(r) => r.id}
      emptyTitle="No equipment mappings"
      emptyDescription="No provider-to-host mappings are configured."
    />
  );
}
